// Text catalog for the Byggeledelse / Construction management service page.
// Icons and images live in src/pages/ByggeledelsePage.tsx and are aligned by
// index with the card arrays here.

import { type Locale } from "../config";
import { type PageMeta } from "../head";

type Card = { title: string; text: string };
type PhaseCard = { title: string; items: string[] };

type ByggeledelseStrings = {
  meta: PageMeta;
  hero: { eyebrow: string; title: string; body: string; ctaPrimary: string; ctaSecondary: string };
  intro: { eyebrow: string; heading: string; p1: string; p2: string; cta: string };
  benefits: { heading: string; intro: string; cards: Card[] };
  phases: { eyebrow: string; heading: string; intro: string };
  phase1: { eyebrow: string; title: string; lead: string; cards: PhaseCard[] };
  phase2: { eyebrow: string; title: string; lead: string; cards: PhaseCard[] };
  phase3: { eyebrow: string; title: string; lead: string; cards: PhaseCard[] };
  quote1: { text: string; attrib: string };
  early: { heading: string; p1: string; p2: string; p3: string; cta: string };
  safety: { heading: string; p1: string; p2: string; p3: string; cta: string };
  quote2: { text: string; attrib: string };
  pharma: { heading: string; p1: string; p2: string; p3: string; cta: string };
  expertise: { heading: string; p1: string; p2: string; leadIn: string; items: string[]; cta: string };
  cta: { heading: string; body: string; call: string; write: string };
};

export const byggeledelseStrings: Record<Locale, ByggeledelseStrings> = {
  da: {
    meta: {
      title: "Byggeledelse - Fokus på tid, kvalitet og økonomi i projektet",
      description:
        "MI Support er eksperter i byggeledelse. Vores byggeledere sikrer, at I kommer i mål til rette tid med den rigtige kvalitet og indenfor budgettet.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK2628_1350x900-1.jpg",
    },
    hero: {
      eyebrow: "Vores ydelser",
      title: "Byggeledelse",
      body: "Med en byggeleder fra MI Support får I en tryg og effektiv styring af jeres byggeprojekt fra de første idéer til det færdige byggeri. I får overblik, fremdrift og en fast sparringspartner, der sikrer, at beslutninger bliver truffet i tide, at samarbejdet mellem alle parter fungerer, og at projektet når i mål til tiden med færre risici og større tryghed.",
      ctaPrimary: "Tag en uforpligtende snak",
      ctaSecondary: "Se alle ydelser",
    },
    intro: {
      eyebrow: "Fra idé til handover",
      heading: "Få en stærk sparringspartner fra start til slut",
      p1: "I MI Support har vi erfaring med at varetage alle størrelser af opgaver. Nogle af vores samarbejdspartnere bygger større, komplicerede produktionsanlæg i den farmaceutiske industri, og her er vi med i både design-, anlægs- og byggefasen. Andre skal have hjælp til at få et mindre projekt i mål og gøres klar til handover.",
      p2: "Vi tager altid udgangspunkt i hver enkelt sag og finder den helt rette konsulent til at løse opgaven, så I får det bedst mulige resultat.",
      cta: "Kontakt os og hør mere",
    },
    benefits: {
      heading: "Hvad får I med en byggeleder fra MI Support?",
      intro:
        "Vores byggeledere sikrer struktur, overblik og kvalitet i hele byggeprocessen, lige fra designfasen til endelig handover.",
      cards: [
        {
          title: "Fuld indsigt i projektets fremdrift",
          text: "I får løbende overblik over projektets status, økonomi og eventuelle udfordringer. Det giver jer et solidt beslutningsgrundlag og tryghed i, at projektet udvikler sig som planlagt uden ubehagelige overraskelser.",
        },
        {
          title: "Kontrol over ændringer og økonomi",
          text: "Ændringer opstår i de fleste byggeprojekter. Vi sikrer, at konsekvenser for tid, økonomi og kvalitet bliver afklaret og dokumenteret, før der træffes beslutninger. Det giver jer fuld kontrol over projektet og minimerer risikoen for uforudsete omkostninger.",
        },
        {
          title: "Effektiv koordinering med alle parter",
          text: "Når mange forskellige aktører skal arbejde sammen, er god koordinering afgørende. Vi sørger for, at alle arbejder mod samme mål, så misforståelser, ventetid og fejl reduceres. Resultatet er et mere effektivt byggeforløb, højere kvalitet og større sikkerhed for, at projektet afleveres som aftalt.",
        },
      ],
    },
    phases: {
      eyebrow: "Byggelederens rolle",
      heading: "Byggelederens rolle i projektet",
      intro:
        "Vores byggeledere tager ansvar i hele projektets livscyklus med struktur, overblik og fokus på kvalitet i hver fase. Byggelederens opgaver og ansvarsområder kan variere afhængigt af projektets størrelse og kompleksitet.",
    },
    phase1: {
      eyebrow: "Fase 01",
      title: "Forberedelsesfasen",
      lead: "I forberedelsestiden kan en byggeleder fra MI Support blandt andet stå for:",
      cards: [
        {
          title: "Udbudsstrategi",
          items: [
            "Rådgivning om udbudsstrategi",
            "Udarbejdelse af udbudsmateriale og bilag",
            "Evaluering og sammenligning af indkomne tilbud",
            "Udarbejdelse af kontraktgrundlag med entreprenører",
          ],
        },
        {
          title: "Kvalitetssikring",
          items: [
            "Kvalitetssikring af projektmateriale",
            "Gennemgang af myndighedsgodkendelser og tilladelser",
            "Sikring af compliance med bygningsreglementet og standarder",
            "Koordinering og strukturering af projektdokumentation",
          ],
        },
        {
          title: "Projektgennemgang",
          items: [
            "Gennemgang af projektmateriale med teknisk fagtilsyn",
            "Afklaring af tekniske grænseflader mellem fag",
            "Risikovurdering af kritiske konstruktioner og processer",
            "Koordinering med rådgiverholdet for tidlig fejlfinding",
          ],
        },
      ],
    },
    phase2: {
      eyebrow: "Fase 02",
      title: "Udførelsesfasen",
      lead: "I udførelsesfasen dækker vores byggeledere et bredt felt af opgaver:",
      cards: [
        {
          title: "Møder & koordinering",
          items: [
            "Opstarts- og projektgennemgangsmøder",
            "Tidsplans- og økonomimøder",
            "Ugentlige møder samt rundering før/efter byggemøder",
            "Referater af møder med entreprenører",
          ],
        },
        {
          title: "Økonomi & ekstraarbejder",
          items: [
            "Gennemgang og præsentation af økonomi for bygherre",
            "Vurdering af ekstraarbejde og krav fra entreprenør",
            "Godkendelse af fakturaer på baggrund af stadeopgørelser",
          ],
        },
        {
          title: "Kvalitet & tilsyn",
          items: [
            "Løbende kvalitetsgennemgang på byggepladsen",
            "Stikprøvekontrol af tilsynsnotater og tilsynsplan",
            "Gennemgang af udbudskontrolplan, DV- og KS-materiale",
          ],
        },
        {
          title: "Mangler & aflevering",
          items: [
            "Stikprøvekontrol af mangelgennemgang og udbedring",
            "Deltagelse ved mangelgennemgang og afleveringsforretninger",
          ],
        },
        {
          title: "Commissioning & drift",
          items: [
            "Kvalitetssikring af Commissioning Plan",
            "Opfølgning på Commissioning Plan og kontrol af drift",
          ],
        },
        {
          title: "Rapportering & rådgivning",
          items: [
            "Løbende rapportering og fremsendelse af rapport",
            "Rådgivning ifm. konflikthåndtering (ABR89/18 og AB92/18)",
          ],
        },
      ],
    },
    phase3: {
      eyebrow: "Fase 03",
      title: "Handover",
      lead: "Når byggeriet er færdigt, sikrer vores byggeledere en struktureret overlevering til bygherre og drift:",
      cards: [
        {
          title: "Afleveringsforretning",
          items: [
            "Planlægning og gennemførelse af afleveringsforretning",
            "Koordinering mellem bygherre, entreprenører og rådgivere",
          ],
        },
        {
          title: "Mangelopfølgning",
          items: [
            "Registrering og opfølgning på mangler",
            "Kontrol af udbedring inden for aftalte frister",
          ],
        },
        {
          title: "Dokumentation & drift",
          items: [
            "Indsamling og overlevering af DV- og KS-materiale",
            "Sikring af as-built dokumentation til driftsorganisationen",
          ],
        },
      ],
    },
    quote1: {
      text: "Diversitet er et stort fokusområde i MI Support, så det er vigtigt for os, at vores byggeledere kan arbejde sammen med alle uanset uddannelse, seksualitet, etnicitet, sprog eller kulturel baggrund.",
      attrib: "Sonny Christensen — Ejer & stifter af MI Support",
    },
    early: {
      heading: "Inddrag en byggeleder tidligt og kom godt fra start",
      p1: "Ved at inddrage vores byggeledere tidligt i projekteringsfasen, kan vi bidrage med ekspertviden i bygbarhed, arbejdsprocesser og grænsefladekoordinering. Det giver jer et godt og solidt overblik over byggeprojektet og skaber en tryg byggeproces.",
      p2: "MI Supports byggeledere har fokus på at planlægge arbejdsprocesser, der bliver gennemgået på ugentlige tidsplanmøder sammen med alle involverede parter.",
      p3: "Det er byggelederens ansvar at administrere de indgående kontrakter og varetage bygherres interesser overfor interessenter.",
      cta: "Få hjælp af en kvalificeret byggeleder",
    },
    safety: {
      heading: "Fokus på et sikkert arbejdsmiljø",
      p1: "Hos MI Support tager vi arbejdsmiljø seriøst. Vi har et team af sikkerhedskoordinatorer, der står klar til at tage hånd om sikkerheden på byggepladsen. Alle vores arbejdsmiljøkoordinatorer er uddannede og har erfaring inden for alle led af byggeriet.",
      p2: "Ved at benytte vores arbejdsmiljøkoordinatorer i projekteringsfasen sikrer I, at byggeriet vil være muligt at arbejde på uden risiko for nedslidning eller ulykker.",
      p3: "I udførelsesfasen sikrer vi, at bygherres forpligtelser overholdes, og at byggepladsen er sikker for alle, der færdes der. Vi har en dialogbaseret tilgang og inddrager alle aktører på byggepladsen.",
      cta: "Læs mere om arbejdsmiljø og sikkerhed",
    },
    quote2: {
      text: "Vi anser vores byggeledere som bindeleddet fra idéfase til realisering. De samler alle leddene i byggeriet og er den røde tråd med ansvar og overblik fra start til slut.",
      attrib: "Simon Christensen — Sr. projektleder & medejer af MI Support",
    },
    pharma: {
      heading: "Byggeledelse til jeres Pharma-byggeri",
      p1: "Mange af MI Supports byggeledere er specialiseret i byggeledelse inden for den farmaceutiske industri, herunder alle de processer, der skal til for at planlægge, designe, bygge og vedligeholde de nødvendige faciliteter til at producere og teste lægemidler og medicinsk udstyr.",
      p2: "Den farmaceutiske industri har strenge krav til renhed, sikkerhed og hygiejne, og bygninger og udstyr skal overholde en række standarder og reguleringer for at sikre, at de producerede produkter er sikre og effektive.",
      p3: "Vores byggeledere indgår i tæt samarbejde med både arkitekter og ingeniører for at sikre, at alle aspekter af byggeriet opfylder de nødvendige standarder og krav.",
      cta: "Læs mere om vores samarbejdspartnere",
    },
    expertise: {
      heading: "Vores byggeledere har fagligheden i top",
      p1: "Vores byggeledere er som regel uddannede ingeniører eller bygningskonstruktører, og de har stort fokus på tværfagligheden i deres arbejde. De besidder overblikket og har en grundig forståelse af alle faser i byggeprocessen.",
      p2: "Bygningskonstruktører fra MI Support er uddannede til at varetage den praktiske og tekniske projektering fra skitse til hovedprojekt, og dette gøres i samarbejde med projekteringens øvrige parter, f.eks. andre ingeniører, arkitekter og myndigheder.",
      leadIn: "Arbejdet for en byggeleder i MI Support indebærer blandt andet:",
      items: [
        "At bistå både projektering og praktisk byggeledelse",
        "At være central aktør blandt projektudbydere, bygherrer, ingeniører, arkitekter, håndværkere, leverandører, myndigheder og brugere",
        "At skabe overblik over de mange fag og tilknyttede specialister",
        "At benytte aktuelle digitale værktøjer",
      ],
      cta: "Mød vores MI Supportere",
    },
    cta: {
      heading: "Skal MI Support stå for byggeledelsen?",
      body: "Har I brug for en erfaren byggeleder, der kan sikre fremdrift, kvalitet og økonomi på pladsen? Vi tager gerne en uforpligtende snak om jeres projekt og behov.",
      call: "Ring +45 5555 1212",
      write: "Skriv til os",
    },
  },

  en: {
    meta: {
      title: "Construction management - Focus on time, quality and economy",
      description:
        "MI Support are experts in construction management. Our construction managers make sure you reach the finish on time, with the right quality and within budget.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK2628_1350x900-1.jpg",
    },
    hero: {
      eyebrow: "Our services",
      title: "Construction management",
      body: "With a construction manager from MI Support you get safe and efficient management of your construction project, from the first ideas to the finished building. You get overview, momentum and a dedicated sparring partner who ensures decisions are made in time, that collaboration between all parties works, and that the project reaches the finish on time with fewer risks and greater peace of mind.",
      ctaPrimary: "Have a no-obligation chat",
      ctaSecondary: "See all services",
    },
    intro: {
      eyebrow: "From idea to handover",
      heading: "Get a strong sparring partner from start to finish",
      p1: "At MI Support we have experience handling tasks of all sizes. Some of our partners build larger, complex production facilities in the pharmaceutical industry, where we take part in the design, construction and build phases. Others need help getting a smaller project to the finish and ready for handover.",
      p2: "We always take each individual case as our starting point and find exactly the right consultant to solve the task, so you get the best possible result.",
      cta: "Contact us to learn more",
    },
    benefits: {
      heading: "What do you get with a construction manager from MI Support?",
      intro:
        "Our construction managers ensure structure, overview and quality throughout the construction process, from the design phase to final handover.",
      cards: [
        {
          title: "Full insight into the project's progress",
          text: "You get a continuous overview of the project's status, economy and any challenges. This gives you a solid basis for decisions and confidence that the project is developing as planned without unpleasant surprises.",
        },
        {
          title: "Control over changes and economy",
          text: "Changes arise in most construction projects. We ensure that consequences for time, economy and quality are clarified and documented before decisions are made. This gives you full control over the project and minimises the risk of unforeseen costs.",
        },
        {
          title: "Efficient coordination with all parties",
          text: "When many different parties have to work together, good coordination is crucial. We make sure everyone works towards the same goal, so misunderstandings, waiting time and errors are reduced. The result is a more efficient construction process, higher quality and greater certainty that the project is delivered as agreed.",
        },
      ],
    },
    phases: {
      eyebrow: "The construction manager's role",
      heading: "The construction manager's role in the project",
      intro:
        "Our construction managers take responsibility throughout the project's life cycle with structure, overview and a focus on quality in every phase. The construction manager's tasks and responsibilities can vary depending on the project's size and complexity.",
    },
    phase1: {
      eyebrow: "Phase 01",
      title: "The preparation phase",
      lead: "During the preparation period, a construction manager from MI Support can, among other things, handle:",
      cards: [
        {
          title: "Tender strategy",
          items: [
            "Advice on tender strategy",
            "Preparation of tender material and appendices",
            "Evaluation and comparison of received bids",
            "Preparation of contract basis with contractors",
          ],
        },
        {
          title: "Quality assurance",
          items: [
            "Quality assurance of project material",
            "Review of authority approvals and permits",
            "Ensuring compliance with the building regulations and standards",
            "Coordination and structuring of project documentation",
          ],
        },
        {
          title: "Project review",
          items: [
            "Review of project material with technical supervision",
            "Clarification of technical interfaces between disciplines",
            "Risk assessment of critical structures and processes",
            "Coordination with the advisory team for early fault finding",
          ],
        },
      ],
    },
    phase2: {
      eyebrow: "Phase 02",
      title: "The execution phase",
      lead: "In the execution phase, our construction managers cover a broad field of tasks:",
      cards: [
        {
          title: "Meetings & coordination",
          items: [
            "Kick-off and project review meetings",
            "Schedule and economy meetings",
            "Weekly meetings and walkarounds before/after construction meetings",
            "Minutes of meetings with contractors",
          ],
        },
        {
          title: "Economy & extra work",
          items: [
            "Review and presentation of economy for the client",
            "Assessment of extra work and claims from the contractor",
            "Approval of invoices based on progress statements",
          ],
        },
        {
          title: "Quality & supervision",
          items: [
            "Ongoing quality review on the construction site",
            "Spot checks of supervision notes and supervision plan",
            "Review of tender control plan, O&M and QA material",
          ],
        },
        {
          title: "Defects & handover",
          items: [
            "Spot checks of defect review and remediation",
            "Participation in defect reviews and handover proceedings",
          ],
        },
        {
          title: "Commissioning & operation",
          items: [
            "Quality assurance of the Commissioning Plan",
            "Follow-up on the Commissioning Plan and control of operation",
          ],
        },
        {
          title: "Reporting & advisory",
          items: [
            "Ongoing reporting and submission of reports",
            "Advice on conflict management (ABR89/18 and AB92/18)",
          ],
        },
      ],
    },
    phase3: {
      eyebrow: "Phase 03",
      title: "Handover",
      lead: "When the construction is complete, our construction managers ensure a structured handover to the client and operations:",
      cards: [
        {
          title: "Handover proceedings",
          items: [
            "Planning and execution of the handover proceedings",
            "Coordination between client, contractors and advisors",
          ],
        },
        {
          title: "Defect follow-up",
          items: [
            "Registration and follow-up of defects",
            "Control of remediation within agreed deadlines",
          ],
        },
        {
          title: "Documentation & operation",
          items: [
            "Collection and handover of O&M and QA material",
            "Securing as-built documentation for the operations organisation",
          ],
        },
      ],
    },
    quote1: {
      text: "Diversity is a major focus area at MI Support, so it's important to us that our construction managers can work with everyone regardless of education, sexuality, ethnicity, language or cultural background.",
      attrib: "Sonny Christensen — Owner & founder of MI Support",
    },
    early: {
      heading: "Involve a construction manager early and get off to a good start",
      p1: "By involving our construction managers early in the design phase, we can contribute expert knowledge in buildability, work processes and interface coordination. This gives you a good and solid overview of the construction project and creates a safe construction process.",
      p2: "MI Support's construction managers focus on planning work processes that are reviewed at weekly schedule meetings together with all involved parties.",
      p3: "It is the construction manager's responsibility to administer the contracts entered into and to safeguard the client's interests towards stakeholders.",
      cta: "Get help from a qualified construction manager",
    },
    safety: {
      heading: "Focus on a safe working environment",
      p1: "At MI Support we take the working environment seriously. We have a team of safety coordinators ready to take care of safety on the construction site. All our working environment coordinators are trained and have experience across every stage of construction.",
      p2: "By using our working environment coordinators in the design phase, you ensure that the construction will be possible to work on without risk of wear-and-tear injuries or accidents.",
      p3: "In the execution phase we ensure that the client's obligations are met and that the construction site is safe for everyone present. We have a dialogue-based approach and involve all parties on the site.",
      cta: "Read more about working environment and safety",
    },
    quote2: {
      text: "We consider our construction managers the link from idea phase to realisation. They bring together all the parts of the construction and are the common thread with responsibility and overview from start to finish.",
      attrib: "Simon Christensen — Sr. project manager & co-owner of MI Support",
    },
    pharma: {
      heading: "Construction management for your pharma build",
      p1: "Many of MI Support's construction managers specialise in construction management within the pharmaceutical industry, including all the processes needed to plan, design, build and maintain the facilities required to produce and test medicines and medical devices.",
      p2: "The pharmaceutical industry has strict requirements for cleanliness, safety and hygiene, and buildings and equipment must comply with a range of standards and regulations to ensure that the products produced are safe and effective.",
      p3: "Our construction managers work closely with both architects and engineers to ensure that all aspects of the construction meet the necessary standards and requirements.",
      cta: "Read more about our partners",
    },
    expertise: {
      heading: "Our construction managers have top professional skills",
      p1: "Our construction managers are usually trained engineers or architectural technologists, and they place great emphasis on interdisciplinarity in their work. They have the overview and a thorough understanding of all phases of the construction process.",
      p2: "Architectural technologists from MI Support are trained to handle the practical and technical design from sketch to main project, and this is done in collaboration with the other parties in the design, e.g. other engineers, architects and authorities.",
      leadIn: "The work of a construction manager at MI Support involves, among other things:",
      items: [
        "Assisting with both design and practical construction management",
        "Being a central actor among project owners, clients, engineers, architects, tradespeople, suppliers, authorities and users",
        "Creating an overview of the many disciplines and associated specialists",
        "Using current digital tools",
      ],
      cta: "Meet our MI Supporters",
    },
    cta: {
      heading: "Should MI Support handle the construction management?",
      body: "Do you need an experienced construction manager who can ensure momentum, quality and economy on site? We're happy to have a no-obligation chat about your project and needs.",
      call: "Call +45 5555 1212",
      write: "Write to us",
    },
  },

  de: {
    meta: {
      title: "Baumanagement - Zeit, Qualität und Wirtschaftlichkeit",
      description:
        "MI Support ist Experte für Baumanagement. Unsere Bauleiter sorgen dafür, dass Sie termingerecht, in der richtigen Qualität und im Budget ans Ziel kommen.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK2628_1350x900-1.jpg",
    },
    hero: {
      eyebrow: "Unsere Leistungen",
      title: "Baumanagement",
      body: "Mit einem Bauleiter von MI Support erhalten Sie eine sichere und effiziente Steuerung Ihres Bauprojekts – von den ersten Ideen bis zum fertigen Gebäude. Sie erhalten Überblick, Fortschritt und einen festen Sparringspartner, der dafür sorgt, dass Entscheidungen rechtzeitig getroffen werden, die Zusammenarbeit aller Parteien funktioniert und das Projekt termingerecht mit weniger Risiken und größerer Sicherheit ans Ziel kommt.",
      ctaPrimary: "Unverbindliches Gespräch führen",
      ctaSecondary: "Alle Leistungen ansehen",
    },
    intro: {
      eyebrow: "Von der Idee zur Übergabe",
      heading: "Erhalten Sie einen starken Sparringspartner von Anfang bis Ende",
      p1: "Bei MI Support haben wir Erfahrung mit Aufgaben jeder Größe. Einige unserer Partner bauen größere, komplexe Produktionsanlagen in der pharmazeutischen Industrie, und hier sind wir in der Design-, Anlagen- und Bauphase dabei. Andere brauchen Hilfe, um ein kleineres Projekt ans Ziel zu bringen und für die Übergabe vorzubereiten.",
      p2: "Wir gehen immer von jedem einzelnen Fall aus und finden genau den richtigen Berater zur Lösung der Aufgabe, sodass Sie das bestmögliche Ergebnis erhalten.",
      cta: "Kontaktieren Sie uns für mehr Informationen",
    },
    benefits: {
      heading: "Was erhalten Sie mit einem Bauleiter von MI Support?",
      intro:
        "Unsere Bauleiter sorgen für Struktur, Überblick und Qualität im gesamten Bauprozess – von der Designphase bis zur endgültigen Übergabe.",
      cards: [
        {
          title: "Voller Einblick in den Projektfortschritt",
          text: "Sie erhalten laufend einen Überblick über Status, Wirtschaftlichkeit und etwaige Herausforderungen des Projekts. Das gibt Ihnen eine solide Entscheidungsgrundlage und die Sicherheit, dass sich das Projekt wie geplant entwickelt – ohne unangenehme Überraschungen.",
        },
        {
          title: "Kontrolle über Änderungen und Wirtschaftlichkeit",
          text: "Änderungen entstehen in den meisten Bauprojekten. Wir stellen sicher, dass Folgen für Zeit, Wirtschaftlichkeit und Qualität geklärt und dokumentiert werden, bevor Entscheidungen getroffen werden. Das gibt Ihnen volle Kontrolle über das Projekt und minimiert das Risiko unvorhergesehener Kosten.",
        },
        {
          title: "Effiziente Koordination mit allen Parteien",
          text: "Wenn viele verschiedene Akteure zusammenarbeiten müssen, ist gute Koordination entscheidend. Wir sorgen dafür, dass alle auf dasselbe Ziel hinarbeiten, sodass Missverständnisse, Wartezeiten und Fehler reduziert werden. Das Ergebnis ist ein effizienterer Bauablauf, höhere Qualität und größere Sicherheit, dass das Projekt wie vereinbart übergeben wird.",
        },
      ],
    },
    phases: {
      eyebrow: "Die Rolle des Bauleiters",
      heading: "Die Rolle des Bauleiters im Projekt",
      intro:
        "Unsere Bauleiter übernehmen Verantwortung im gesamten Lebenszyklus des Projekts – mit Struktur, Überblick und Fokus auf Qualität in jeder Phase. Aufgaben und Verantwortungsbereiche des Bauleiters können je nach Größe und Komplexität des Projekts variieren.",
    },
    phase1: {
      eyebrow: "Phase 01",
      title: "Die Vorbereitungsphase",
      lead: "In der Vorbereitungszeit kann ein Bauleiter von MI Support unter anderem Folgendes übernehmen:",
      cards: [
        {
          title: "Ausschreibungsstrategie",
          items: [
            "Beratung zur Ausschreibungsstrategie",
            "Erstellung von Ausschreibungsunterlagen und Anlagen",
            "Bewertung und Vergleich eingegangener Angebote",
            "Erstellung der Vertragsgrundlage mit Auftragnehmern",
          ],
        },
        {
          title: "Qualitätssicherung",
          items: [
            "Qualitätssicherung des Projektmaterials",
            "Prüfung von Behördengenehmigungen und Erlaubnissen",
            "Sicherstellung der Compliance mit der Bauordnung und Normen",
            "Koordination und Strukturierung der Projektdokumentation",
          ],
        },
        {
          title: "Projektprüfung",
          items: [
            "Prüfung des Projektmaterials mit technischer Fachaufsicht",
            "Klärung technischer Schnittstellen zwischen den Gewerken",
            "Risikobewertung kritischer Konstruktionen und Prozesse",
            "Koordination mit dem Beraterteam zur frühen Fehlersuche",
          ],
        },
      ],
    },
    phase2: {
      eyebrow: "Phase 02",
      title: "Die Ausführungsphase",
      lead: "In der Ausführungsphase decken unsere Bauleiter ein breites Aufgabenfeld ab:",
      cards: [
        {
          title: "Besprechungen & Koordination",
          items: [
            "Auftakt- und Projektdurchsprachebesprechungen",
            "Termin- und Wirtschaftlichkeitsbesprechungen",
            "Wöchentliche Besprechungen sowie Rundgänge vor/nach Baubesprechungen",
            "Protokolle der Besprechungen mit Auftragnehmern",
          ],
        },
        {
          title: "Wirtschaftlichkeit & Mehrarbeiten",
          items: [
            "Prüfung und Präsentation der Wirtschaftlichkeit für den Bauherrn",
            "Bewertung von Mehrarbeit und Forderungen des Auftragnehmers",
            "Freigabe von Rechnungen auf Basis von Leistungsständen",
          ],
        },
        {
          title: "Qualität & Aufsicht",
          items: [
            "Laufende Qualitätsprüfung auf der Baustelle",
            "Stichprobenkontrolle von Aufsichtsnotizen und Aufsichtsplan",
            "Prüfung von Ausschreibungskontrollplan, Betriebs- und QS-Material",
          ],
        },
        {
          title: "Mängel & Abnahme",
          items: [
            "Stichprobenkontrolle von Mängelprüfung und Mängelbeseitigung",
            "Teilnahme an Mängelprüfungen und Abnahmen",
          ],
        },
        {
          title: "Commissioning & Betrieb",
          items: [
            "Qualitätssicherung des Commissioning Plans",
            "Nachverfolgung des Commissioning Plans und Kontrolle des Betriebs",
          ],
        },
        {
          title: "Berichtswesen & Beratung",
          items: [
            "Laufendes Berichtswesen und Übersendung von Berichten",
            "Beratung beim Konfliktmanagement (ABR89/18 und AB92/18)",
          ],
        },
      ],
    },
    phase3: {
      eyebrow: "Phase 03",
      title: "Übergabe",
      lead: "Wenn der Bau fertig ist, sorgen unsere Bauleiter für eine strukturierte Übergabe an Bauherrn und Betrieb:",
      cards: [
        {
          title: "Abnahme",
          items: [
            "Planung und Durchführung der Abnahme",
            "Koordination zwischen Bauherr, Auftragnehmern und Beratern",
          ],
        },
        {
          title: "Mängelnachverfolgung",
          items: [
            "Erfassung und Nachverfolgung von Mängeln",
            "Kontrolle der Beseitigung innerhalb vereinbarter Fristen",
          ],
        },
        {
          title: "Dokumentation & Betrieb",
          items: [
            "Sammlung und Übergabe von Betriebs- und QS-Material",
            "Sicherung der As-built-Dokumentation für die Betriebsorganisation",
          ],
        },
      ],
    },
    quote1: {
      text: "Diversität ist bei MI Support ein großer Schwerpunkt, daher ist es uns wichtig, dass unsere Bauleiter mit allen zusammenarbeiten können – unabhängig von Ausbildung, Sexualität, Ethnizität, Sprache oder kulturellem Hintergrund.",
      attrib: "Sonny Christensen — Inhaber & Gründer von MI Support",
    },
    early: {
      heading: "Beziehen Sie einen Bauleiter früh ein und kommen Sie gut in Gang",
      p1: "Indem wir unsere Bauleiter früh in der Planungsphase einbeziehen, können wir Expertenwissen zu Baubarkeit, Arbeitsprozessen und Schnittstellenkoordination beitragen. Das gibt Ihnen einen guten und soliden Überblick über das Bauprojekt und schafft einen sicheren Bauprozess.",
      p2: "Die Bauleiter von MI Support legen Wert auf die Planung von Arbeitsprozessen, die bei wöchentlichen Terminbesprechungen gemeinsam mit allen Beteiligten durchgegangen werden.",
      p3: "Es liegt in der Verantwortung des Bauleiters, die geschlossenen Verträge zu verwalten und die Interessen des Bauherrn gegenüber den Beteiligten zu wahren.",
      cta: "Holen Sie sich Hilfe von einem qualifizierten Bauleiter",
    },
    safety: {
      heading: "Fokus auf ein sicheres Arbeitsumfeld",
      p1: "Bei MI Support nehmen wir das Arbeitsumfeld ernst. Wir haben ein Team von Sicherheitskoordinatoren, das bereitsteht, sich um die Sicherheit auf der Baustelle zu kümmern. Alle unsere Arbeitsumweltkoordinatoren sind ausgebildet und haben Erfahrung in allen Phasen des Baus.",
      p2: "Durch den Einsatz unserer Arbeitsumweltkoordinatoren in der Planungsphase stellen Sie sicher, dass am Bau ohne Risiko von Verschleiß oder Unfällen gearbeitet werden kann.",
      p3: "In der Ausführungsphase stellen wir sicher, dass die Pflichten des Bauherrn eingehalten werden und die Baustelle für alle, die sich dort aufhalten, sicher ist. Wir haben einen dialogbasierten Ansatz und beziehen alle Akteure auf der Baustelle ein.",
      cta: "Mehr über Arbeitsumfeld und Sicherheit erfahren",
    },
    quote2: {
      text: "Wir betrachten unsere Bauleiter als das Bindeglied von der Ideenphase bis zur Realisierung. Sie führen alle Teile des Baus zusammen und sind der rote Faden mit Verantwortung und Überblick von Anfang bis Ende.",
      attrib: "Simon Christensen — Sr. Projektleiter & Mitinhaber von MI Support",
    },
    pharma: {
      heading: "Baumanagement für Ihren Pharma-Bau",
      p1: "Viele Bauleiter von MI Support sind auf Baumanagement in der pharmazeutischen Industrie spezialisiert, einschließlich aller Prozesse, die nötig sind, um die für die Herstellung und Prüfung von Arzneimitteln und Medizinprodukten erforderlichen Einrichtungen zu planen, zu entwerfen, zu bauen und zu warten.",
      p2: "Die pharmazeutische Industrie hat strenge Anforderungen an Reinheit, Sicherheit und Hygiene, und Gebäude und Anlagen müssen eine Reihe von Normen und Vorschriften einhalten, um sicherzustellen, dass die hergestellten Produkte sicher und wirksam sind.",
      p3: "Unsere Bauleiter arbeiten eng mit Architekten und Ingenieuren zusammen, um sicherzustellen, dass alle Aspekte des Baus die erforderlichen Normen und Anforderungen erfüllen.",
      cta: "Mehr über unsere Partner erfahren",
    },
    expertise: {
      heading: "Unsere Bauleiter haben höchste fachliche Kompetenz",
      p1: "Unsere Bauleiter sind in der Regel ausgebildete Ingenieure oder Bautechniker und legen großen Wert auf Interdisziplinarität in ihrer Arbeit. Sie haben den Überblick und ein gründliches Verständnis aller Phasen des Bauprozesses.",
      p2: "Bautechniker von MI Support sind ausgebildet, die praktische und technische Planung von der Skizze bis zum Hauptprojekt zu übernehmen, und zwar in Zusammenarbeit mit den übrigen Beteiligten der Planung, z. B. anderen Ingenieuren, Architekten und Behörden.",
      leadIn: "Die Arbeit eines Bauleiters bei MI Support umfasst unter anderem:",
      items: [
        "Unterstützung sowohl der Planung als auch der praktischen Bauleitung",
        "Zentraler Akteur unter Projektträgern, Bauherren, Ingenieuren, Architekten, Handwerkern, Lieferanten, Behörden und Nutzern zu sein",
        "Überblick über die vielen Gewerke und die zugehörigen Spezialisten zu schaffen",
        "Aktuelle digitale Werkzeuge zu nutzen",
      ],
      cta: "Lernen Sie unsere MI Supporter kennen",
    },
    cta: {
      heading: "Soll MI Support das Baumanagement übernehmen?",
      body: "Benötigen Sie einen erfahrenen Bauleiter, der Fortschritt, Qualität und Wirtschaftlichkeit auf der Baustelle sicherstellt? Wir führen gern ein unverbindliches Gespräch über Ihr Projekt und Ihre Bedürfnisse.",
      call: "Anrufen +45 5555 1212",
      write: "Schreiben Sie uns",
    },
  },
};
